/**
 * Admin Cleanup 0 — återanvändbara tillstånd för adminsidor:
 * laddning, tomvy och riktiga felvyer (statuskod + förklaring + åtgärd).
 * Inga eviga spinners, inga fel som bara är toast.
 */
import { AlertTriangle, ArrowLeft, Inbox, Loader2, RefreshCw } from "lucide-react";

export function AdminLoading({ label = "Laddar…" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-gray-500" data-testid="admin-loading">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function AdminEmpty({ title = "Inget här ännu", message, children }) {
  return (
    <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center" data-testid="admin-empty">
      <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
      <p className="text-sm font-semibold text-gray-700">{title}</p>
      {message && <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">{message}</p>}
      {children}
    </div>
  );
}

const STATUS_EXPLANATIONS = {
  401: "Du är inte inloggad eller så har sessionen gått ut. Logga in igen.",
  403: "Ditt konto saknar behörighet för den här vyn.",
  404: "Resursen finns inte — den kan ha tagits bort eller så är länken fel.",
  500: "Ett serverfel inträffade. Försök igen om en stund.",
  502: "Servern svarar inte just nu. Försök igen om en stund.",
  503: "Tjänsten är tillfälligt otillgänglig. Försök igen om en stund.",
  0: "Nätverksfel — kontrollera din anslutning och försök igen.",
};

export function AdminError({ what = "Innehållet", status = null, message, onRetry, onBack, backLabel = "Gå tillbaka" }) {
  const explanation = message
    || STATUS_EXPLANATIONS[status]
    || (status >= 500 ? STATUS_EXPLANATIONS[500] : "Ett oväntat fel inträffade.");
  return (
    <div className="bg-white border border-red-200 rounded-xl p-8 text-center max-w-lg mx-auto" data-testid="admin-error">
      <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
      <p className="text-sm font-semibold text-gray-900" data-testid="admin-error-what">
        {what} kunde inte laddas
      </p>
      {status !== null && status !== 0 && (
        <p className="text-xs font-mono text-red-500 mt-1" data-testid="admin-error-status">Statuskod: {status}</p>
      )}
      {(status === 0 || status === null) && (
        <p className="text-xs font-mono text-red-500 mt-1" data-testid="admin-error-status">Nätverksfel</p>
      )}
      <p className="text-xs text-gray-500 mt-2" data-testid="admin-error-explanation">{explanation}</p>
      <div className="flex items-center justify-center gap-2 mt-5">
        {onRetry && (
          <button onClick={onRetry} data-testid="admin-error-retry" className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-gray-700">
            <RefreshCw className="w-3.5 h-3.5" /> Försök igen
          </button>
        )}
        {onBack && (
          <button onClick={onBack} data-testid="admin-error-back" className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50">
            <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
          </button>
        )}
      </div>
    </div>
  );
}
